import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faLock,
  faFileInvoice,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import logo from "../images/EnvironmentalLabsLogoHorizontal.png";
import CommonLoader from "./common/CommonLoader";
import {
  Box,
  Button,
  Divider,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  Authenticate,
  SignUp,
  SetPassword,
  ResetPasswordRequest,
  ResetPasswordResponse,
} from "../services/userService";
import { faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import ReactGA from "react-ga";
import LogRocket from "logrocket";
import { Link, useNavigate } from "react-router-dom";

ReactGA.initialize("UA-193251731-1");

// Shared chrome for every auth form so the login, sign-up and reset screens match.
// Defined at module scope on purpose: declaring it inside Login would give React a
// new component type on every render, remounting the form and wiping the inputs.
const AuthCard = ({ children, onSubmit }) => (
  <Box
    component={onSubmit ? "form" : "div"}
    onSubmit={onSubmit}
    sx={{ width: "100%" }}
  >
    <Box sx={{ textAlign: "center", mb: 3 }}>
      <Box
        component="img"
        src={logo}
        alt="logo"
        sx={{ maxWidth: "100%", height: "auto" }}
      />
    </Box>
    {children}
  </Box>
);

// userService throws the parsed response body, so `error` can be a ProblemDetails
// object ({type,title,status,traceId}) rather than a string. Rendering that object
// directly crashes React with "Objects are not valid as a React child", which took
// out the whole login screen on a failed sign-in. Coerce it to something printable.
const ErrorText = ({ error }) => {
  if (!error) return null;

  const text =
    typeof error === "string"
      ? error
      : error.title ||
        error.message ||
        "Login failed. Please check your details and try again.";

  return (
    <Typography color="error" variant="body2" sx={{ mb: 1 }}>
      {text}
    </Typography>
  );
};

export default function Login({ setToken }) {
  const [username, setUsername] = useState();
  const [password, setPassword] = useState();
  const [loading, setLoading] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [error, setError] = useState();
  const [signUp, setSignUp] = useState(false);
  const [createPassword, setCreatePassword] = useState(false);
  const [account, setAccount] = useState();
  const [showPasswords, setShowPasswords] = useState(false);
  const [showPasswordResetScreen, setShowPasswordResetScreen] = useState(false);
  const [tempToken, setTempToken] = useState();
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [responseError, setResponseError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    const path = window.location.pathname;
    if (path.includes("/reset-password")) {
      setShowPasswordResetScreen(true);
      // Extract the token from the URL
      const parts = path.split("/");
      const tokenIndex = parts.indexOf("reset-password") + 1;
      if (tokenIndex < parts.length) {
        const token = parts[tokenIndex];
        setTempToken(token);
      }
    }
  }, []);

  useEffect(() => {
    setPassword("");
  }, [signUp]);

  const handleLogin = async (e) => {
    e.preventDefault();

    // Enter submits the form, so this covers the keyboard path too.
    if (!username?.trim() || !password?.trim()) {
      setError("Please enter your account number or email, and your password.");
      return;
    }
    setError(undefined);

    ReactGA.event({
      category: "User",
      action: `Logged in. Account ${username}}`,
    });

    setLoading(true);
    try {
      const body = await Authenticate(username, password);
      if (body) {
        LogRocket.identify(body.name);
        setToken(body);
      }
    } catch (err) {
      if (err) setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await SignUp(username, account);

      if (response) {
        setTempToken(response);
        setCreatePassword(true);
      }
    } catch (err) {
    } finally {
    }
    setLoading(false);
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    try {
      const response = await SetPassword(username, password, tempToken);

      if (response) {
        setCreatePassword(false);
        setSignUp(false);
        setError("Password set. Please log in.");
      }
    } catch (err) {
    } finally {
    }
  };
  const handleResetPasswordClicked = (e) => {    
    e.preventDefault();
    localStorage.clear();
    setToken(null);
    setIsResetPassword(true);
  };
  const passwordAdornments = {
    startAdornment: (
      <InputAdornment position="start">
        <FontAwesomeIcon icon={faLock} />
      </InputAdornment>
    ),
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={() => setShowPasswords(!showPasswords)}
          edge="end"
          size="small"
          aria-label="toggle password visibility"
        >
          <FontAwesomeIcon icon={showPasswords ? faEyeSlash : faEye} />
        </IconButton>
      </InputAdornment>
    ),
  };

  const RenderLogin = () => {
    return (
      <AuthCard onSubmit={handleLogin}>
        <TextField
          label="Account Number or Email"
          placeholder="e.g. 1111"
          fullWidth
          size="small"
          required
          onChange={(e) => setUsername(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FontAwesomeIcon icon={faEnvelope} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Password"
          type={showPasswords ? "text" : "password"}
          placeholder="*******"
          autoComplete="current-password"
          fullWidth
          size="small"
          required
          onChange={(e) => setPassword(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={passwordAdornments}
        />

        <ErrorText error={error} />

        <Button
          type="submit"
          fullWidth
          size="large"
          variant="contained"
          disableElevation
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Login
        </Button>

        <Box sx={{ textAlign: "right", mt: 1 }}>
          <Button
            size="small"
            component={Link}
            to="#"
            onClick={(e) => handleResetPasswordClicked(e)}
          >
            Reset Password
          </Button>
        </Box>

        <Divider sx={{ my: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Have multiple accounts?
          </Typography>
        </Divider>

        <Button
          fullWidth
          variant="outlined"
          sx={{ mb: 1.5 }}
          onClick={(e) => {
            e.preventDefault();
            setSignUp(true);
          }}
        >
          Sign Up Here
        </Button>

        <Button
          fullWidth
          component="a"
          href="https://envirotestkits.com/safe-home-test-kit-registration/"
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          color="warning"
        >
          Register Test Kit
        </Button>
      </AuthCard>
    );
  };

  const renderSetPassword = () => {
    return (
      <Box component="form" onSubmit={handleSetPassword}>
        <TextField
          label="New Password"
          type={showPasswords ? "text" : "password"}
          fullWidth
          size="small"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={passwordAdornments}
        />

        <Box sx={{ textAlign: "center" }}>
          {/* This button previously had a `cclassName` typo, so it rendered unstyled. */}
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            Set Password
          </Button>
        </Box>
      </Box>
    );
  };

  const renderSignUp = () => {
    const handleCancel = () => {
      window.location.reload();
    };

    return (
      <AuthCard>
        {createPassword === true ? (
          renderSetPassword()
        ) : (
          <Box component="form" onSubmit={handleSignUp}>
            <TextField
              label="Email"
              fullWidth
              size="small"
              required
              onChange={(e) => setUsername(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Any of your account numbers (enter only one)"
              fullWidth
              size="small"
              required
              onChange={(e) => setAccount(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FontAwesomeIcon icon={faFileInvoice} />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              size="large"
              variant="contained"
              disableElevation
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              Sign up
            </Button>
            <Button fullWidth type="button" sx={{ mt: 1 }} onClick={handleCancel}>
              Cancel
            </Button>
          </Box>
        )}
      </AuthCard>
    );
  };

  const renderResetPassword = () => {
    const handleResetPassword = async (e) => {
      e.preventDefault();
      setResetLoading(true);
      try {
        const body = await ResetPasswordRequest(resetEmail);
        setResetLoading(false);
        setIsResetPassword(false);
        alert("Password reset link has been sent to your email.");
      } catch (error) {
        console.error(
          "Error occurred while sending password reset link:",
          error
        );
      }
    };

    const handleCancel = () => {
      window.location.reload();
    };

    return (
      <AuthCard onSubmit={handleResetPassword}>
        <TextField
          label="Account Number or Email"
          placeholder="Account or Email"
          fullWidth
          size="small"
          required
          value={resetEmail}
          onChange={(e) => setResetEmail(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FontAwesomeIcon icon={faEnvelope} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          size="large"
          variant="contained"
          disableElevation
          disabled={resetLoading}
          startIcon={
            resetLoading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Reset Password
        </Button>
        <Button fullWidth type="button" sx={{ mt: 1 }} onClick={handleCancel}>
          Cancel
        </Button>
      </AuthCard>
    );
  };

  const renderResetPasswordResponse = () => {
    const handleResetPasswordResponse = async (e) => {
      e.preventDefault();
      if (newPassword !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
      if (
        newPassword.length < 4 ||
        confirmPassword.length < 4 ||
        newPassword.length > 8 ||
        confirmPassword.length > 8
      ) {
        setError("Passwords must be between 4-8 characters long.");
        return;
      }

      setSubmitting(true);
      setError("");
      try {
        const response = await ResetPasswordResponse(tempToken, newPassword);
        console.log("Reset password response:", response);
        alert("Password reset successfully.");
        window.labOrderViewWindow = window.open("/");
        setNewPassword("");
        setConfirmPassword("");
        setResponseError("");
      } catch (error) {
        console.error("Error resetting password:", error);
        setResponseError("An error occurred while resetting password.");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <AuthCard onSubmit={handleResetPasswordResponse}>
        <TextField
          label="Password"
          type={showPasswords ? "text" : "password"}
          placeholder="*******"
          autoComplete="new-password"
          fullWidth
          size="small"
          required
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={passwordAdornments}
        />

        <TextField
          label="Confirm Password"
          type={showPasswords ? "text" : "password"}
          placeholder="*******"
          autoComplete="new-password"
          fullWidth
          size="small"
          required
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={passwordAdornments}
        />

        <ErrorText error={error} />

        <Button
          type="submit"
          fullWidth
          size="large"
          variant="contained"
          disableElevation
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Update
        </Button>
      </AuthCard>
    );
  };

  return (
    <>
      {showPasswordResetScreen
        ? renderResetPasswordResponse()
        : isResetPassword
        ? renderResetPassword()
        : signUp
        ? renderSignUp()
        : RenderLogin()}

      <CommonLoader loading={loading || resetLoading} />
    </>
  );
}

Login.propTypes = {
  setToken: PropTypes.func.isRequired,
};
